import { AdControls } from '@post-office/components--ad-controls';

import { cssMap, cx } from '@atlaskit/css';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { token } from '@atlaskit/tokens';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageEvent } from '@post-office/message-lifecycle';

import { type MouseEvent, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { type JSX } from 'react/jsx-runtime';

import { JSM_REQUEST_RESOLVER_ACTION_SUBJECT } from '../constants';

import { messages } from '../messages';
import { RequestResolverLogo } from './RequestResolverLogo.tsx';

const styles = cssMap({
	boxContainer: {
		display: 'flex',
		width: '100%',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		alignItems: 'flex-start',
		gap: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.information'),
		placeContent: 'center',
	},
	contentStack: {
		gap: token('space.100'),
		width: '100%',
	},
	headerRow: {
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	ctaHeading: {
		font: token('font.heading.xsmall'),
		margin: token('space.0'),
	},
	ctaLink: {
		backgroundColor: token('color.background.neutral.subtle'),
		padding: token('space.0'),
		borderRadius: token('radius.xsmall'),
		color: token('color.link'),
		cursor: 'pointer',
		font: token('font.body.small'),
		textAlign: 'left',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
});

const linkUrl =
	'https://support.atlassian.com/jira-service-management-cloud/docs/about-request-resolver?utm_source=jira&utm_medium=cta&utm_campaign=jsm_request_resolver_side_nav';

const PROJECT_KEY_PATTERN = /\/(?:projects|browse)\/([A-Z][A-Z0-9_]*)/;

const getProjectKeyFromLocation = (): string | undefined => {
	if (typeof globalThis.window === 'undefined') {
		return undefined;
	}

	return PROJECT_KEY_PATTERN.exec(globalThis.window.location.pathname)?.[1];
};

const getProjectScopedUrl = (projectKey: string) =>
	`${globalThis.window.location.origin}/jira/servicedesk/projects/${projectKey}/settings/ai-agents`;

type JsmRequestResolverMessageProps = {
	context: {
		source: string;
	};
	handleOnDismissed: () => void;
};

export const JsmRequestResolverMessage = ({
	context,
	handleOnDismissed,
}: JsmRequestResolverMessageProps): JSX.Element | null => {
	const { source } = context;

	const { formatMessage } = useIntl();
	const { messageClicked } = useMessageEvent();
	const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const [destinationUrl, setDestinationUrl] = useState(linkUrl);

	// The placement is server-rendered and the side nav outlives route changes without re-rendering
	// this component, so refresh from the current location before the browser acts on the link.
	const refreshDestinationUrl = useCallback(() => {
		const projectKey = getProjectKeyFromLocation();
		setDestinationUrl(projectKey ? getProjectScopedUrl(projectKey) : linkUrl);
	}, []);

	useEffect(() => {
		refreshDestinationUrl();
	}, [refreshDestinationUrl]);

	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'rendered',
			actionSubject: JSM_REQUEST_RESOLVER_ACTION_SUBJECT,
			attributes: {
				source,
				engagementType: 'view',
			},
		});
	}, [fireAnalyticsEvent, source]);

	const handleDismiss = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'dismissed',
			actionSubject: JSM_REQUEST_RESOLVER_ACTION_SUBJECT,
			attributes: {
				source,
				engagementType: 'dismiss',
			},
		});
		void messageClicked();
		void onClosed({ interactionIdentifier: 'dismiss', additionalMetadata: { source } });
		handleOnDismissed();
	}, [fireAnalyticsEvent, source, messageClicked, onClosed, handleOnDismissed]);

	const handleLinkClick = useCallback(() => {
		const projectKey = getProjectKeyFromLocation();

		void messageClicked();
		void onCtaActioned({
			interactionIdentifier: 'requestResolverLink',
			destinationUrl: projectKey ? getProjectScopedUrl(projectKey) : linkUrl,
			additionalMetadata: { source },
		});
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: JSM_REQUEST_RESOLVER_ACTION_SUBJECT,
			attributes: {
				source,
				engagementType: 'click',
				hasProjectContext: Boolean(projectKey),
			},
		});
	}, [source, messageClicked, onCtaActioned, fireAnalyticsEvent]);

	const handleLinkAuxClick = useCallback(
		(clickEvent: MouseEvent<HTMLAnchorElement>) => {
			// Middle-click opens the href in a new tab but does not fire onClick.
			if (clickEvent.button === 1) {
				handleLinkClick();
			}
		},
		[handleLinkClick],
	);

	// The docs fallback leaves the product, so it opens in a new tab to keep Jira in place. The
	// project-scoped settings URL stays in the current tab.
	const opensInNewTab = destinationUrl === linkUrl;

	return (
		<Box xcss={cx(styles.boxContainer)} testId="jsm-request-resolver-side-nav-banner">
			<Stack xcss={cx(styles.contentStack)} testId="jsm-request-resolver-side-nav-banner-content">
				<Inline xcss={cx(styles.headerRow)}>
					<RequestResolverLogo />
					<AdControls
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							reasons: [
								<FormattedMessage key="reason-1" {...messages.default.whyAmISeeingThisReason} />,
							],
						}}
						onDismissed={handleDismiss}
						spacing="compact"
					/>
				</Inline>
				<Box as="h3" xcss={cx(styles.ctaHeading)}>
					{formatMessage(messages.default.ctaTitle)}
				</Box>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(messages.default.ctaDescription)}
				</Text>
				<Anchor
					href={destinationUrl}
					target={opensInNewTab ? '_blank' : undefined}
					rel={opensInNewTab ? 'noopener noreferrer' : undefined}
					onClick={handleLinkClick}
					onAuxClick={handleLinkAuxClick}
					onPointerDown={refreshDestinationUrl}
					onFocus={refreshDestinationUrl}
					testId="jsm-request-resolver-side-nav-banner-link"
					xcss={cx(styles.ctaLink)}
				>
					{formatMessage(messages.default.ctaLink)}
				</Anchor>
			</Stack>
		</Box>
	);
};
